import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSend, FiX, FiDollarSign, FiZap } from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import { useAIChat } from '../../hooks/useAIChat';
import { useConversationStore } from '../../stores/conversationStore';
import ErrorBoundary from '../ErrorBoundary';
import { logger } from '../../utils/logger';
import toast from 'react-hot-toast';

interface AIChatProps {
  conversationId: string;
}

const AIChat: React.FC<AIChatProps> = ({ conversationId }) => {
  const [input, setInput] = useState('');
  const [estimatedCost, setEstimatedCost] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { messages } = useConversationStore();
  const {
    isStreaming,
    streamingMessage,
    usage,
    isAvailable,
    sendStreamingMessage,
    cancelStreaming,
    estimateCost,
    checkAvailability,
  } = useAIChat(conversationId);

  // Check availability on mount
  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // Update cost estimate as user types
  useEffect(() => {
    if (input.length > 0) {
      const cost = estimateCost(input);
      setEstimatedCost(cost);
    } else {
      setEstimatedCost(0);
    }
  }, [input, estimateCost]);

  const handleSendMessage = async () => {
    if (!input.trim() || isStreaming || !isAvailable) return;

    const messageContent = input.trim();
    setInput('');

    try {
      await sendStreamingMessage(messageContent);
    } catch (error) {
      logger.error('Failed to send AI message', error as Error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isAvailable) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <BiBot className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          AI Chat Not Available
        </h3>
        <p className="text-gray-500 mb-4">
          The AI service is not configured. Please contact your administrator.
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md">
          <p className="text-sm text-yellow-800">
            <strong>Admin:</strong> Add OPENAI_API_KEY to environment variables to enable AI chat.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      resetKeys={[conversationId]}
      onError={(error, errorInfo) => {
        logger.error('AI Chat component error', error, { componentStack: errorInfo.componentStack });
        toast.error('AI Chat encountered an error. Please refresh the page.');
      }}
    >
      <div className="flex flex-col h-full bg-gray-50">
        {/* Messages Area */}
        <div
          className="flex-1 overflow-y-auto p-4 space-y-4"
          role="log"
          aria-label="AI conversation messages"
          aria-live="polite"
          aria-relevant="additions"
        >
          <AnimatePresence>
            {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              role="article"
              aria-label={`${message.role === 'user' ? 'Your message' : 'AI Assistant message'} at ${new Date(message.timestamp).toLocaleTimeString()}`}
            >
              <div
                className={`max-w-[70%] rounded-lg px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-800'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-1">
                    <BiBot className="w-4 h-4 text-blue-600" aria-hidden="true" />
                    <span className="text-xs font-medium text-gray-500">AI Assistant</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap break-words">{message.content}</p>
                <span className="text-xs opacity-70 mt-1 block" aria-label={`Sent at ${new Date(message.timestamp).toLocaleTimeString()}`}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Streaming Message */}
        {isStreaming && streamingMessage && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
            role="status"
            aria-live="assertive"
            aria-label="AI Assistant is typing a response"
          >
            <div className="max-w-[70%] rounded-lg px-4 py-2 bg-white border border-gray-200 text-gray-800">
              <div className="flex items-center gap-2 mb-1">
                <BiBot className="w-4 h-4 text-blue-600 animate-pulse" aria-hidden="true" />
                <span className="text-xs font-medium text-gray-500">AI Assistant (typing...)</span>
              </div>
              <p className="whitespace-pre-wrap break-words">{streamingMessage}</p>
              <div className="flex items-center gap-2 mt-2" aria-hidden="true">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse delay-75" />
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse delay-150" />
              </div>
            </div>
          </motion.div>
        )}

        {/* Typing Indicator (when no stream yet) */}
        {isStreaming && !streamingMessage && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="rounded-lg px-4 py-3 bg-white border border-gray-200">
              <div className="flex items-center gap-2">
                <BiBot className="w-4 h-4 text-blue-600 animate-pulse" />
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-75" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-150" />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Usage Stats */}
      {usage && (
        <div className="px-4 py-2 bg-blue-50 border-t border-blue-100">
          <div className="flex items-center justify-between text-xs text-blue-700">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <FiZap className="w-3 h-3" />
                <span>{usage.totalTokens} tokens</span>
              </div>
              <div className="flex items-center gap-1">
                <FiDollarSign className="w-3 h-3" />
                <span>~${((usage.promptTokens / 1000) * 0.01 + (usage.completionTokens / 1000) * 0.03).toFixed(4)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-gray-200" role="form" aria-label="AI chat input">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label htmlFor="ai-chat-input" className="sr-only">
              Type your message to the AI assistant
            </label>
            <textarea
              id="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isStreaming ? "AI is responding..." : "Type a message... (Enter to send)"}
              disabled={isStreaming}
              rows={1}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              aria-label="Message to AI assistant"
              aria-describedby={input.trim() && estimatedCost > 0 ? "cost-estimate" : undefined}
            />
            {input.trim() && estimatedCost > 0 && (
              <div
                id="cost-estimate"
                className="flex items-center gap-1 mt-1 text-xs text-gray-500"
                role="status"
                aria-live="polite"
              >
                <FiDollarSign className="w-3 h-3" aria-hidden="true" />
                <span>Est. cost: ${estimatedCost.toFixed(4)}</span>
              </div>
            )}
          </div>

          {isStreaming ? (
            <button
              onClick={cancelStreaming}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              aria-label="Stop AI response"
              title="Stop AI response"
            >
              <FiX className="w-5 h-5" aria-hidden="true" />
              <span>Stop</span>
            </button>
          ) : (
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || isStreaming}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Send message to AI assistant"
              title="Send message"
            >
              <FiSend className="w-5 h-5" aria-hidden="true" />
              <span>Send</span>
            </button>
          )}
        </div>
      </div>
      </div>
    </ErrorBoundary>
  );
};

export default AIChat;
